import { useNavigate, useSearchParams } from 'react-router-dom';

import { Chip } from '@mui/material';

import ProgressBar from '../../components/Graphs/ProgressBar/ProgressBar';
import TableTitle from '../../components/TableTitle/TableTitle';
import ReviewStageIcon from '../../assets/customIcons/reviewStageIcon';
import { getStatusColor, getChipTextColor, getEntityPrefix } from '../../utils/utilFunctions';
import BasicCellText from './BasicCellText';

const ReviewColumns = (isActive) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const columns = [
		{
			field: 'org_name',
			headerName: 'Submitter',
			description: 'Submitter',
			renderCell: (cellValues) => <BasicCellText cellValues={cellValues} />,
			flex: 1
		},
		{
			field: 'project_title',
			headerName: 'Application (Project Title)',
			description: 'Application (Project Title)',
			renderCell: (cellValues) => {
				const formattedApplicationID = `api-${cellValues.row.application_unique_identifier}`;
				const programId = cellValues.row.programId;

				const handleCardClick = () =>
					navigate(
						`/programs/p-${programId}/applications/${formattedApplicationID}?formtype=review${
							searchParams.get('iFrame') === 'true' ? '&iFrame=true' : ''
						}`
					);

				const titleData = {
					displayId: formattedApplicationID,
					title: cellValues.row.project_title,
					entityType: 'Application Instance'
				};

				return <TableTitle handleCardClick={handleCardClick} titleData={titleData} />;
			},
			flex: 1,
			minWidth: 250
		}
	];

	if (isActive) {
		columns.push({
			field: 'stage',
			headerName: 'Active Stage',
			description: 'Active Stage',
			renderCell: (cellValues) => {
				const currentStage = cellValues.row.form_stage_id;

				if (currentStage) {
					const entityType =
						currentStage.stage_type === 'application' ? 'submission-stage' : 'evaluation-stage';

					const entityData = {
						type: entityType,
						isInstance: true
					};

					if (currentStage.name && entityType && currentStage.unique_identifier) {
						cellValues.formattedValue = `${currentStage.name} [${getEntityPrefix(entityData)}${
							currentStage.unique_identifier
						}]`;

						return (
							<BasicCellText
								cellValues={cellValues}
								StartIcon={ReviewStageIcon}
								IconStatus={currentStage.status}
							/>
						);
					}
				}
			},
			flex: 1,
			minWidth: 260
		});
	}

	columns.push({
		field: 'review_form',
		headerName: 'Review Form',
		description: 'Review Form',
		renderCell: (cellValues) => {
			const formattedReviewID = `rfi-${cellValues.row.review_unique_identifier}`;

			const titleData = {
				displayId: formattedReviewID,
				title: cellValues.row.label,
				entityType: 'Review Form',
				status: cellValues.row.status
			};

			return <TableTitle titleData={titleData} />;
		},
		flex: 1
	});

	columns.push({
		field: 'review_form_status',
		headerName: 'Review Form Status',
		description: 'Review Form Status',
		renderCell: (cellValues) => {
			switch (cellValues.row.status) {
				case 'Draft':
					cellValues.formattedValue = 'In Progress';
					break;
				case 'Completed':
					cellValues.formattedValue = 'Submitted';
					break;
				default:
					cellValues.formattedValue = cellValues.row.status;
					break;
			}

			return (
				<Chip
					label={cellValues.formattedValue}
					sx={{
						backgroundColor: getStatusColor(cellValues.formattedValue),
						color: getChipTextColor(cellValues.formattedValue)
					}}
					size="small"
				/>
			);
		},
		flex: 1
	});

	if (isActive) {
		columns.push({
			field: 'remainingTime',
			headerName: 'Remaining Time',
			renderCell: (cellValues) => {
				const currentStage = cellValues.row.form_stage_id;
				const status = cellValues.row.status;

				if (currentStage && status !== 'Completed') {
					const start_date = currentStage.start_date;
					const end_date = currentStage.end_date;
					const startDate = new Date(start_date?.split('T')?.[0] + 'T00:00:00');
					const endDate = new Date(end_date?.split('T')?.[0] + 'T00:00:00');
					const currentDate = new Date();

					const remainingTimeData = {
						startDate,
						endDate,
						isCountdown: true,
						isForReview: true,
						isUnavailable: currentDate < startDate,
						isExpired: currentDate > endDate
					};

					return <ProgressBar testTitle={'manageApplication'} data={remainingTimeData} />;
				}
			},
			flex: 1
		});
	}

	columns.push({
		field: 'created_on',
		type: 'date',
		headerName: 'Started',
		description: 'Started',
		renderCell: (cellValues) => {
			const row = cellValues.row;
			const started = row.created_on && row.status !== 'Not Started';

			cellValues.formattedValue = started ? row.created_on : 'Not Started';

			return <BasicCellText isDate={started} cellValues={cellValues} />;
		},
		flex: 1
	});

	columns.push({
		field: 'last_updated_on',
		type: 'date',
		headerName: 'Last Edited',
		description: 'Last Edited',
		renderCell: (cellValues) => {
			const row = cellValues.row;
			const started = row.last_updated_on && row.status !== 'Not Started';

			if (!started) {
				cellValues.formattedValue = 'Not Started';
			}

			return <BasicCellText isDate={started} cellValues={cellValues} />;
		},
		flex: 1
	});

	return columns;
};

export default ReviewColumns;
